"use client";

import PageBreadCrumb from "@/components/common/PageBreadCrumb";
import PurchaseChallanForm from "@/components/forms/PurchaseChallanForm";
import ConcreteChallanImportModal from "@/components/purchase/ConcreteChallanImportModal";
import Button from "@/components/ui/button/Button";
import { useQueryClient } from "@tanstack/react-query";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import React from "react";

function parseKind(raw) {
  const k = typeof raw === "string" ? raw.trim().toLowerCase() : "";
  if (k === "regular") return "REGULAR";
  if (k === "concrete") return "CONCRETE";
  return null;
}

export default function PurchaseChallanNewFlow() {
  const searchParams = useSearchParams();
  const qc = useQueryClient();
  const kind = parseKind(searchParams.get("kind"));
  const [concreteImportOpen, setConcreteImportOpen] = React.useState(false);

  if (!kind) {
    return (
      <div className="space-y-6">
        <ConcreteChallanImportModal
          isOpen={concreteImportOpen}
          onClose={() => setConcreteImportOpen(false)}
          onImported={() => qc.invalidateQueries({ queryKey: ["purchaseChallans"] })}
        />
        <PageBreadCrumb pageTitle="New Purchase Challan" />
        <div className="rounded-xl border border-gray-800 bg-gray-950/40 p-6">
          <p className="mb-1 text-base font-medium text-white">Choose purchase type</p>
          <p className="mb-6 text-sm text-gray-400">
            Regular and concrete challans use different fields (concrete adds quarry and royalty). Pick one to open the
            matching form.
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            <Link
              href="/purchase/challans/new?kind=regular"
              className="rounded-lg border border-gray-700 bg-gray-900/80 p-5 transition hover:border-emerald-600/50 hover:bg-gray-900"
            >
              <p className="text-base font-semibold text-white">Regular purchase</p>
              <p className="mt-2 text-xs leading-relaxed text-gray-400">
                Standard raw-material purchase — no quarry or royalty on the challan.
              </p>
            </Link>
            <div className="flex flex-col rounded-lg border border-gray-700 bg-gray-900/80 transition hover:border-emerald-600/50">
              <Link href="/purchase/challans/new?kind=concrete" className="block flex-1 p-5 hover:bg-gray-900">
                <p className="text-base font-semibold text-white">Concrete purchase</p>
                <p className="mt-2 text-xs leading-relaxed text-gray-400">
                  Concrete flow — quarry selection and royalty number after you choose party and material.
                </p>
              </Link>
              <div className="border-t border-gray-800 px-5 pb-4 pt-0">
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full"
                  onClick={(ev) => {
                    ev.preventDefault();
                    setConcreteImportOpen(true);
                  }}
                >
                  Import Excel
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const titleSuffix = kind === "CONCRETE" ? "Concrete" : "Regular";

  return (
    <div className="space-y-6">
      <PageBreadCrumb pageTitle={`New ${titleSuffix} Purchase Challan`} />
      <PurchaseChallanForm fixedPurchaseKind={kind} />
    </div>
  );
}
